import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  InventoryPerProduct,
  fetchZonesByLocationId,
  InventoryTransfers,
  getInventorySpots,
  getRcks,
  getShelves,
  getSpots,
  InventoryBatches,
} from "../../../server/InventoryUpdate";
import { useSelector } from "react-redux";
import ProcductLocationTable from "./ProcductLocationTable.js";
import ProductDetailsPopUp from "../inventoryUpdate/ProductDetailsPopUp.js";
import { useLocation } from "react-router-dom";

const ProductLocationPopUp = ({ product }) => {
  const {
    stationId,
    selectedProduct,
    items: rawItems,
  } = useSelector((state) => ({
    stationId: state.user.stationId,
    selectedProduct: state.selectedProduct.product,
    items: state.displayProduct.itemsShow,
  }));

  const items = Array.isArray(rawItems) ? rawItems : [];

  let resolvedProduct = product;
  if (!resolvedProduct && selectedProduct) {
    const matchingProduct = items.find(
      (item) => item.gtin === selectedProduct.gtin,
    );
    resolvedProduct = matchingProduct || selectedProduct;
  }

  const [location, setLocation] = useState([]);
  const [locations, setLocations] = useState(null);
  const [hasReturn, setHasReturn] = useState(false);
  const [selectedSrc, setSelectedSrc] = useState(null);
  const [newQuantity, setNewQuantity] = useState(1);
  const [whereHouse, setWhereHouse] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedWarehouseRow, setSelectedWarehouseRow] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedCellStokeType, setSelectedCellStokeType] = useState(null);
  const [detailsPopUp, setDetailsPopUp] = useState(false);
  const [inventorySpots, setInventorySpots] = useState([]);
  const [warehouseData, setWarehouseData] = useState([]);
  const [racks, setRacks] = useState([]);
  const [shelves, setShelves] = useState([]);
  const [spots, setSpots] = useState([]);
  const [inventoryBatch, setInventoryBatch] = useState(null);
  const [loadingBatch, setLoadingBatch] = useState(true);

  const pathname = useLocation().pathname;
  const mountedRef = useRef(true);

  const productBarcod = resolvedProduct?.productId ?? null;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadZones = async () => {
      try {
        const res = await fetchZonesByLocationId(stationId);
        if (cancelled) return;
        const data = res?.data || [];
        setLocations(data);
        const salesZone = data.filter((loc) => loc.type === "sales-zone");
        setWarehouseData(salesZone.length > 0 ? salesZone : []);
      } catch (err) {
        console.error("Error fetching zones:", err);
      }
    };

    loadZones();
    return () => {
      cancelled = true;
    };
  }, [stationId]);

  useEffect(() => {
    let cancelled = false;

    const loadInventory = async () => {
      try {
        const res = await InventoryPerProduct(productBarcod, stationId);
        if (cancelled) return;
        const itemsRes = res?.data?.items || [];
        setLocation(itemsRes);
        setHasReturn(true);
      } catch (err) {
        console.error("Error loading InventoryPerProduct:", err);
      }
    };

    loadInventory();
    return () => {
      cancelled = true;
    };
  }, [productBarcod, stationId]);

  useEffect(() => {
    let cancelled = false;

    const loadInventoryBatch = async () => {
      setLoadingBatch(true);
      try {
        const res = await InventoryBatches(productBarcod, stationId);
        if (cancelled) return;

        const items = res?.data ?? [];
        setInventoryBatch(items[0] ?? null);
      } catch (err) {
        console.error("Error loading InventoryBatches:", err);
        setInventoryBatch(null);
      } finally {
        if (!cancelled) setLoadingBatch(false);
      }
    };

    loadInventoryBatch();
    return () => {
      cancelled = true;
    };
  }, [inventorySpots, productBarcod, stationId]);

  useEffect(() => {
    if (!warehouseData || warehouseData.length === 0) {
      setRacks([]);
      return;
    }

    let cancelled = false;
    const zoneId = warehouseData[0].id;

    const loadRacks = async () => {
      try {
        const res = await getRcks(zoneId);
        if (cancelled) return;
        setRacks(res?.data || []);
      } catch (err) {
        console.error("Error fetching racks:", err);
        setRacks([]);
      }
    };

    loadRacks();
    return () => (cancelled = true);
  }, [warehouseData]);

  useEffect(() => {
    if (!selectedWarehouse || !warehouseData[0]) {
      setShelves([]);
      return;
    }

    let cancelled = false;
    const zoneId = warehouseData[0].id;
    const rackId = selectedWarehouse.rackId;
    const param = `${zoneId}-${rackId}`;

    const loadShelves = async () => {
      try {
        const res = await getShelves(param);
        if (cancelled) return;
        setShelves(res?.data || []);
      } catch (err) {
        console.error("Error fetching shelves:", err);
        setShelves([]);
      }
    };

    loadShelves();
    return () => (cancelled = true);
  }, [selectedWarehouse, warehouseData]);

  useEffect(() => {
    if (!selectedWarehouseRow || !selectedWarehouse || !warehouseData[0]) {
      setSpots([]);
      return;
    }

    let cancelled = false;
    const zoneId = warehouseData[0].id;
    const rackId = selectedWarehouse.rackId;
    const spotId = selectedWarehouseRow.rackShelfId;
    const param = `${zoneId}-${rackId}-${spotId}`;

    const loadSpots = async () => {
      try {
        const res = await getSpots(param);
        if (cancelled) return;
        setSpots(res?.data || []);
      } catch (err) {
        console.error("Error fetching spots:", err);
        setSpots([]);
      }
    };

    loadSpots();
    return () => (cancelled = true);
  }, [selectedWarehouseRow, selectedWarehouse, warehouseData]);

  const handleRowClick = useCallback(
    (row) => {
      setSelectedSrc(row);
      setNewQuantity(row.quantity);
      if (row.StokeTypeId !== selectedCellStokeType) {
        setSelectedCellStokeType(row.StokeTypeId);
      }
    },
    [selectedCellStokeType],
  );

  const locationsMap = useMemo(() => {
    if (!locations || !Array.isArray(locations)) return new Map();
    const m = new Map();
    locations.forEach((l) => m.set(l.name, l.id));
    return m;
  }, [locations]);

  const addNewLocation = useCallback(
    async (dest) => {
      try {
        const invRes = await getInventorySpots(dest);
        setInventorySpots(invRes?.items || []);

        let selectedDst;
        if (whereHouse) {
          if (!selectedCell) return;
          const zone = warehouseData[0]?.id;
          if (!zone) return;
          selectedDst = `${zone}-${selectedCell.rackShelfSpotId}`;
        } else {
          const destId = locationsMap.get(dest);
          if (!destId) {
            return;
          }
          selectedDst = destId;
          if (dest === "מיון") {
            selectedDst = `${selectedDst}-1-01-01`;
          } else if (dest === "כניסה" || dest === "איזור פגומים") {
            selectedDst = `${selectedDst}-01-01-01`;
          }
        }

        const batchId = location?.[0]?.batchId;
        if (!batchId) {
          console.error("missing batchId, cannot transfer");
        }

        const newData = {
          batchId,
          quantity: newQuantity,
          from:
            selectedSrc === -1
              ? `${locationsMap.get("מיון")}-1-01-01`
              : selectedSrc?.spotId || selectedSrc,
          to: selectedDst,
        };

        InventoryTransfers(newData)
          .then(() => {
            InventoryPerProduct(selectedProduct?.productId, stationId).then(
              (x) => {
                setLocation(x.data.items);
                setHasReturn(true);
              },
            );
          })
          .catch((err) => {
            console.error("Error updating location:", err);

            if (err.response) {
              const data = err.response.data;
              try {
                const _parsed = JSON.parse(data.error);
              } catch {
                console.log("NOT JSON. Trying to inspect:", data.error);
              }
            }

            const newData = {
              batchId,
              quantity: newQuantity,
              from:
                selectedSrc === -1
                  ? `${locationsMap.get("מיון")}-1-01-01`
                  : selectedSrc?.spotId || selectedSrc,
              to: selectedDst,
            };

            InventoryTransfers(newData)
              .then(() => {
                InventoryPerProduct(productBarcod, stationId).then((x) => {
                  setLocation(x.data.items);
                  setHasReturn(true);
                });
              })
              .catch((err) => {
                console.error("Error updating location:", err);

                if (err.response) {
                  const data = err.response.data;
                  try {
                    const _parsed = JSON.parse(data.error);
                  } catch {
                    console.log("NOT JSON. Trying to inspect:", data.error);
                  }
                }
              });
            setSelectedSrc(null);
            closeWarehouseModal();
            setNewQuantity(1);
          });
      } catch (err) {
        console.error("Error in addNewLocation:", err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      whereHouse,
      selectedCell,
      warehouseData,
      newQuantity,
      selectedSrc,
      location,
      locationsMap,
      product,
    ],
  );

  const handleQuantityChange = useCallback(
    (value) => {
      const maxQuantity =
        selectedSrc && selectedSrc.quantity ? selectedSrc.quantity : Infinity;
      if (
        selectedSrc === -1 ||
        selectedSrc?.FullLocationName === "מיון" ||
        (Number.isFinite(value) && value >= 1 && value <= maxQuantity)
      ) {
        setNewQuantity(value);
      }
    },
    [selectedSrc],
  );

  const closeWarehouseModal = useCallback(() => {
    setWhereHouse(false);
    setSelectedWarehouse(null);
    setSelectedWarehouseRow(null);
    setSelectedCell(null);
  }, []);

  const handleInvAdj = useCallback(() => {
    setSelectedSrc(-1);
    setNewQuantity(1);
  }, []);

  if (!resolvedProduct) {
    return <div>לא נמצא פריט להצגה</div>;
  }

  return (
    <>
      <div className="product-location">
        <h3 className="product-header">
          {loadingBatch ? (
            "טוען נתוני מלאי..."
          ) : inventoryBatch ? (
            <>
              כמות מקורית: {inventoryBatch.originalQuantity} | כמות זמינה:{" "}
              {inventoryBatch.availableQuantity} | כמות שמורה:{" "}
              {inventoryBatch.reservedQuantity} | כמות מוחזקת:{" "}
              {inventoryBatch.heldQuantity} | כמות פגומה:{" "}
              {inventoryBatch.damagedQuantity}
            </>
          ) : (
            "לא נמצאו נתוני מלאי"
          )}
        </h3>
        <button className="btn" onClick={() => setDetailsPopUp(true)}>
          פרטי מוצר
        </button>
        {detailsPopUp && (
          <ProductDetailsPopUp
            CloseProductDetails={() => setDetailsPopUp(false)}
          />
        )}
      </div>

      <div className="tables">
        <div className="half-side">
          <ProcductLocationTable
            locations={location}
            generalZones={locations}
            selectedRow={selectedSrc}
            hasReturn={hasReturn}
            handleRowClick={handleRowClick}
          />
          {pathname.startsWith("/edit-product/") && (
            <button className="inv-adj-btn" onClick={handleInvAdj}>
              העברה ממיון
            </button>
          )}
        </div>

        {pathname.startsWith("/edit-product/") && selectedSrc && (
          <div className="half-side">
            <ProcductLocationTable
              locations={location.filter((loc) => loc.id !== selectedSrc.id)}
              generalZones={locations}
              selectedRow={selectedSrc}
              hasReturn={hasReturn}
              handleRowClick={addNewLocation}
            />

            <div className="add-row">
              <div className="quantity-input">
                <button onClick={() => handleQuantityChange(newQuantity - 1)}>
                  -
                </button>
                <input
                  type="number"
                  value={newQuantity}
                  onChange={(e) =>
                    handleQuantityChange(parseInt(e.target.value, 10) || 0)
                  }
                />
                <button onClick={() => handleQuantityChange(newQuantity + 1)}>
                  +
                </button>
              </div>

              <button className="btn" onClick={() => addNewLocation("מיון")}>
                מיון
              </button>
              <button
                className="btn"
                onClick={() => addNewLocation("איזור פגומים")}
              >
                החזרות
              </button>
              <button
                className="btn"
                onClick={() => addNewLocation("איזור פגומים")}
              >
                איבודים
              </button>
              <button className="btn" onClick={() => addNewLocation("כניסה")}>
                מחסן כניסה
              </button>
              <button className="btn" onClick={() => setWhereHouse(true)}>
                מחסן
              </button>
            </div>
          </div>
        )}
      </div>

      {whereHouse && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close" onClick={closeWarehouseModal}>
              ×
            </button>
            <h3>בחירת מיקום</h3>
            <h4>בחר טור:</h4>
            <div className="cell-selection">
              {racks.map((warehouse, index) => (
                <button
                  className={"cell-button"}
                  key={warehouse.rackId}
                  onClick={() => {
                    setSelectedWarehouse(warehouse);
                    setSelectedWarehouseRow(null);
                    setSelectedCell(null);
                  }}
                  style={{
                    backgroundColor:
                      selectedWarehouse?.rackId === warehouse.rackId
                        ? "var(--color-info, var(--c-info))"
                        : "var(--hz-surface)",
                  }}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            {selectedWarehouse && (
              <>
                <h4>בחר שורה:</h4>
                <div className="cell-selection">
                  {shelves?.map((row, index) => (
                    <button
                      className="cell-button"
                      key={row.rackShelfId}
                      onClick={() => {
                        setSelectedWarehouseRow(row);
                        setSelectedCell(null);
                      }}
                      style={{
                        backgroundColor:
                          selectedWarehouseRow?.rackShelfId === row.rackShelfId
                            ? "var(--hz-cta)"
                            : "var(--hz-surface)",
                      }}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              </>
            )}

            {selectedWarehouseRow && (
              <>
                <h4>בחר תא:</h4>
                <div className="cell-selection">
                  {spots.map((cell, index) => (
                    <button
                      className="cell-button"
                      style={{
                        backgroundColor:
                          selectedCell?.rackShelfSpotId === cell.rackShelfSpotId
                            ? "var(--color-danger, var(--c-danger))"
                            : "var(--hz-surface)",
                      }}
                      key={cell.rackShelfSpotId}
                      onClick={() => setSelectedCell(cell)}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeWarehouseModal}>
                ביטול
              </button>
              <button
                className={
                  selectedWarehouse && selectedWarehouseRow && selectedCell
                    ? "submit-btn active"
                    : "submit-btn"
                }
                onClick={() => {
                  addNewLocation("איזור מכירה");
                  closeWarehouseModal();
                }}
                disabled={
                  !selectedWarehouse || !selectedWarehouseRow || !selectedCell
                }
              >
                אישור
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductLocationPopUp;
